import { FIELD_NAMES } from '../constants';
import { Project, ProjectLabel } from '../types';
import { hydrateSmsOptOutInstructions } from './smsOptOut';

const {
  NAME,
  LOGO_URL,
  TIME_ZONE,
  PHYSICAL_ADDRESS,
  LABELS,
  GROUP_RECURRING_CAMPAIGNS,
  FIRST_NAME_FIELD,
  LAST_NAME_FIELD,
  USE_DYNAMIC_SENDERS,
  ENABLE_SENDING_DOMAIN,
  SENDING_DOMAIN,
  ADD_UNSUBSCRIBE,
  USE_DOUBLE_OPT_OUT,
  CUSTOM_UNSUBSCRIBE_SNIPPET,
  HOSTED_UNSUBSCRIBE_URL,
  USE_HOSTED_UNSUBSCRIBE_URL_IN_LIST_UNSUBSCRIBE_HEADER,
  SUBSCRIBE_SUCCESS_URL,
  SMS_OPT_OUT_INSTRUCTIONS,
  SMS_DISCLAIMER,
  SMS_SHORTLINKS_MIN_LENGTH_MARKETING,
  SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL,
  WYSIWYG_EDITOR_MODE,
  HMAC_SECRET,
  LOCALES,
  MAPPED_LOCALES,
  TEMPLATE_DEFAULT_LOCALE_MODIFIABLE,
  OVERRIDE_CONTENT_NOT_FOUND_MARKETING,
  OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL,
  ENABLE_ANON_USER_TRACKING,
  ENABLE_GEO_IP_LOOKUP,
  ENABLE_EMAIL_CLICK_IP_UPDATE,
  SHOW_REVENUE,
  ENABLE_IMPROVED_TRACKING,
  USE_FILTERED_TRACKING,
} = FIELD_NAMES;

const visibleLength = 4;
export const maskHMACSHA1 = (hmacString: string) => {
  if (!hmacString) return hmacString;

  if (hmacString.length <= 4) {
    return hmacString; // no masking needed
  }

  const visiblePart = hmacString.slice(0, visibleLength);
  const maskedPart = '*'.repeat(hmacString.length - visibleLength);

  return visiblePart + maskedPart;
};

/**
 * Given that this is such a large form, we are breaking it up into sections
 * and testing it piece by piece. Testing progress for form hydration is
 * documented here. Testing progress for the final step of form submission
 * is documented in the ../components/ProjectSettingsPage.tsx.
 */

export const projectSectionDefaultValues = (project: Project) => ({
  /** Form hydration validated for all fields ✅ */
  [NAME]: project.name,
  [LOGO_URL]: project.logoUrl,
  [TIME_ZONE]: project.timeZone,
  [PHYSICAL_ADDRESS]: project.physicalAddress || '',
});

export const messagingSectionDefaultValues = (project: Project, labels: ProjectLabel[]) => ({
  [LABELS]: labels.map((label) => ({ name: label.name, id: label.id })),
  [GROUP_RECURRING_CAMPAIGNS]: Boolean(project?.extendedProjectSettings?.groupRecurringCampaigns),
});

export const emailSectionDefaultValues = (project: Project) => ({
  /** Form hydration validated for all fields ✅ */
  [FIRST_NAME_FIELD]: project?.firstNameField || '',
  [LAST_NAME_FIELD]: project?.lastNameField || '',
  [USE_DYNAMIC_SENDERS]: Boolean(project?.extendedProjectSettings?.ipTracking?.useDynamicSenders),
  [ENABLE_SENDING_DOMAIN]: Boolean(project?.sendingDomain),
  [SENDING_DOMAIN]: project?.sendingDomain || '',
});

export const emailSubscriptionSectionDefaultValues = (project: Project) => ({
  /** Form hydration validated for all fields ✅ */
  [ADD_UNSUBSCRIBE]: Boolean(project?.addUnsubscribe),
  [USE_DOUBLE_OPT_OUT]: Boolean(project?.extendedProjectSettings?.useDoubleOptOut),
  [CUSTOM_UNSUBSCRIBE_SNIPPET]: project?.extendedProjectSettings?.customUnsubscribeSnippet || '',
  [HOSTED_UNSUBSCRIBE_URL]: project?.hostedUnsubscribeUrl || '',
  [USE_HOSTED_UNSUBSCRIBE_URL_IN_LIST_UNSUBSCRIBE_HEADER]: Boolean(
    project?.extendedProjectSettings?.useHostedUnsubscribeUrlInListUnsubscribeHeader,
  ),
  [SUBSCRIBE_SUCCESS_URL]: project?.subscribeSuccessUrl || '',
});

export const smsSectionDefaultValues = (project: Project) => {
  /** Form hydration validated for all fields ✅ */
  return {
    [SMS_DISCLAIMER]: project?.extendedProjectSettings?.smsDisclaimer || '',
    [SMS_OPT_OUT_INSTRUCTIONS]: hydrateSmsOptOutInstructions(
      project.isSMSDisclaimerOnAllMessages,
      project?.extendedProjectSettings?.disableSMSDisclaimer,
    ),
    [SMS_SHORTLINKS_MIN_LENGTH_MARKETING]: project?.marketingShortLinksLength,
    [SMS_SHORTLINKS_MIN_LENGTH_TRANSACTIONAL]: project?.transactionalShortLinksLength,
  };
};

export const contentSectionDefaultValues = (project: Project) => ({
  /** Form hydration validated for all fields ✅ */
  [WYSIWYG_EDITOR_MODE]: Boolean(project?.extendedProjectSettings?.wysiwygEditorMode),
  [HMAC_SECRET]: project?.extendedProjectSettings?.HMACSecret,
  [LOCALES]: project?.extendedProjectSettings?.locales || [],
  [MAPPED_LOCALES]: project?.extendedProjectSettings?.mappedLocales ?? [],
  [TEMPLATE_DEFAULT_LOCALE_MODIFIABLE]: Boolean(project?.templateDefaultLocaleModifiable),
  [OVERRIDE_CONTENT_NOT_FOUND_MARKETING]: Boolean(
    project?.extendedProjectSettings?.localeSettings?.overrideContentNotFoundMarketing,
  ),
  [OVERRIDE_CONTENT_NOT_FOUND_TRANSACTIONAL]: Boolean(
    project?.extendedProjectSettings?.localeSettings?.overrideContentNotFoundTransactional,
  ),
});

export const audienceSectionDefaultValues = (project: Project) => ({
  /** Form hydration validated for all fields ✅ */
  [ENABLE_ANON_USER_TRACKING]: Boolean(project?.enableAnonUserTracking),
  [ENABLE_GEO_IP_LOOKUP]: Boolean(project?.extendedProjectSettings?.ipTracking?.enableGeoIPLookup),
  [ENABLE_EMAIL_CLICK_IP_UPDATE]: Boolean(
    project?.extendedProjectSettings?.ipTracking?.enableEmailClickIPUpdate,
  ),
});

export const insightsSectionDefaultValues = (project: Project) => ({
  /** Form hydration validated for all fields ✅ */
  [SHOW_REVENUE]: Boolean(project?.extendedProjectSettings?.showRevenue),
  [ENABLE_IMPROVED_TRACKING]: Boolean(project?.extendedProjectSettings?.enableImprovedTracking),
});

export const trackingSectionDefaultValues = (project: Project) => ({
  /** Form hydration validated for all fields ✅ */
  [USE_FILTERED_TRACKING]: Boolean(project?.extendedProjectSettings?.useFilteredTracking),
});

export const createDefaultValues = (project: Project, labels: ProjectLabel[]) => ({
  ...projectSectionDefaultValues(project),
  ...messagingSectionDefaultValues(project, labels),
  ...emailSectionDefaultValues(project),
  ...emailSubscriptionSectionDefaultValues(project),
  ...smsSectionDefaultValues(project),
  ...contentSectionDefaultValues(project),
  ...audienceSectionDefaultValues(project),
  ...insightsSectionDefaultValues(project),
  ...trackingSectionDefaultValues(project),
});
